class AutoGift {
  constructor() {
    this.threshold = 500000; // 5000 rupees in paise (5000 * 100)
    this.giftProductHandle = 'malle-forest-tree';
    this.giftVariantId = null;
    this.giftMessage = 'Congratulations 🎉 We are planting a tree on your behalf';
    this.isProcessing = false;
    this.lastCartTotal = 0;
    
    this.init();
  }

  async init() {
    await this.getGiftVariantId();
    this.hideCartErrors(); // Hide error messages when auto-adding
    setTimeout(() => this.checkAndUpdateCart(), 1000);
    this.monitorCart();
  }

  async getGiftVariantId() {
    try {
      const response = await fetch(`/products/${this.giftProductHandle}.js`);
      if (!response.ok) {
        // Gift product not found – auto-gift feature disabled
        return;
      }
      const product = await response.json();
      if (product.variants && product.variants.length > 0) {
        this.giftVariantId = product.variants[0].id;
      }
    } catch (error) {
      // Silently fail – gift product unavailable
    }
  }

  hideCartErrors() {
    // Hide cart error messages when they appear during auto-gift operations
    const observer = new MutationObserver(() => {
      if (this.isProcessing) {
        const errorMessages = document.querySelectorAll('.cart-item__error, [id*="cart-errors"], .errors');
        errorMessages.forEach(error => {
          if (error.textContent.includes('error while updating')) {
            error.style.display = 'none';
            // Remove the error after a short delay
            setTimeout(() => error.remove(), 100);
          }
        });
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  monitorCart() {
    // Listen for Dawn theme cart updates with debounce
    let cartUpdateTimeout;
    document.addEventListener('cart:updated', () => {
      clearTimeout(cartUpdateTimeout);
      cartUpdateTimeout = setTimeout(() => this.checkAndUpdateCart(), 800);
    });

    // Listen for quantity changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('[name="updates[]"]') || e.target.matches('.quantity__input')) {
        clearTimeout(cartUpdateTimeout);
        cartUpdateTimeout = setTimeout(() => this.checkAndUpdateCart(), 1000);
      }
    });
  }

  async checkAndUpdateCart() {
    if (this.isProcessing || !this.giftVariantId) return;
    
    try {
      const cart = await this.getCart();
      
      // Calculate total excluding the gift tree
      let cartTotalExcludingGift = 0;
      let hasGift = false;
      let giftItem = null;
      
      cart.items.forEach(item => {
        if (item.variant_id === this.giftVariantId) {
          hasGift = true;
          giftItem = item;
        } else {
          cartTotalExcludingGift += item.final_line_price;
        }
      });

      // Only process if cart total has changed significantly
      if (Math.abs(cartTotalExcludingGift - this.lastCartTotal) < 100 && hasGift === (cartTotalExcludingGift >= this.threshold)) {
        return; // No change needed
      }

      this.lastCartTotal = cartTotalExcludingGift;

      // Add gift if threshold reached and not already in cart
      if (cartTotalExcludingGift >= this.threshold && !hasGift) {
        await this.addGiftToCart();
      } 
      // Remove gift if cart drops below threshold
      else if (cartTotalExcludingGift < this.threshold && hasGift) {
        await this.removeGiftFromCart(giftItem);
      }
    } catch (error) {
      // Silently handle cart check errors
    }
  }

  async getCart() {
    const response = await fetch('/cart.js');
    return await response.json();
  }

  async addGiftToCart() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    // Hide any existing error messages
    this.hideExistingErrors();

    try {
      const formData = {
        items: [{
          id: this.giftVariantId,
          quantity: 1
        }]
      };

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (addResponse.ok) {
        
        // Wait a bit before refreshing to avoid race conditions
        await new Promise(resolve => setTimeout(resolve, 300));
        
        await this.refreshCartSections();
        
        // Show notification after cart is updated
        setTimeout(() => {
          this.showNotification(this.giftMessage);
          this.hideExistingErrors(); // Hide any errors that appeared
        }, 500);
      }
    } catch (error) {
      // Silently handle gift add errors
    } finally {
      setTimeout(() => {
        this.isProcessing = false;
      }, 1000);
    }
  }

  async removeGiftFromCart(giftItem) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    this.hideExistingErrors();

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0
        })
      });


      
      await new Promise(resolve => setTimeout(resolve, 300));
      await this.refreshCartSections();
      
      setTimeout(() => {
        this.hideExistingErrors();
      }, 500);
    } catch (error) {
      // Silently handle gift remove errors
    } finally {
      setTimeout(() => {
        this.isProcessing = false;
      }, 1000);
    }
  }

  hideExistingErrors() {
    // Hide any cart error messages
    const errorSelectors = [
      '.cart-item__error',
      '[id*="cart-errors"]',
      '.errors',
      '[role="alert"]',
      '.cart__error'
    ];

    errorSelectors.forEach(selector => {
      const errors = document.querySelectorAll(selector);
      errors.forEach(error => {
        if (error.textContent.includes('error while updating') || 
            error.textContent.includes('Error') ||
            error.textContent.includes('try again')) {
          error.style.display = 'none';
          error.remove();
        }
      });
    });
  }

  async refreshCartSections() {
    try {
      // Use root path to ensure sections are found regardless of current page
      const sections = 'cart-drawer,cart-icon-bubble';
      const response = await fetch(`/?sections=${sections}`);
      if (!response.ok) return;
      const data = await response.json();

      // Update cart drawer if it exists
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && data['cart-drawer']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data['cart-drawer'], 'text/html');
        const newDrawerContent = doc.querySelector('cart-drawer');
        if (newDrawerContent) {
          cartDrawer.innerHTML = newDrawerContent.innerHTML;
        }
      }

      // Update cart items on cart page if it exists
      const cartItems = document.querySelector('cart-items');
      if (cartItems && data['cart-items']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data['cart-items'], 'text/html');
        const newCartItems = doc.querySelector('cart-items');
        if (newCartItems) {
          cartItems.innerHTML = newCartItems.innerHTML;
        }
      }

      // Update cart icon bubble
      const cartBubble = document.querySelector('#cart-icon-bubble');
      if (cartBubble && data['cart-icon-bubble']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data['cart-icon-bubble'], 'text/html');
        const newBubble = doc.querySelector('#cart-icon-bubble');
        if (newBubble) {
          cartBubble.innerHTML = newBubble.innerHTML;
        }
      }

      // Hide any error messages that appeared during refresh
      setTimeout(() => this.hideExistingErrors(), 100);

    } catch (error) {
      // Silently handle section refresh failures
    }
  }

  showNotification(message) {
    // Remove any existing notifications first
    const existingNotifications = document.querySelectorAll('.auto-gift-notification');
    existingNotifications.forEach(n => n.remove());

    const notification = document.createElement('div');
    notification.className = 'auto-gift-notification';
    notification.innerHTML = `
      <div class="auto-gift-notification-content">
        <p>${message}</p>
      </div>
    `;
    
    document.body.appendChild(notification);
    setTimeout(() => notification.classList.add('show'), 100);
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new AutoGift();
  });
} else {
  new AutoGift();
}